import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import Backend from 'i18next-http-backend';
import type { InitOptions } from 'i18next';
import logger from '../utils/logger';

// Supported languages
export const SUPPORTED_LANGUAGES = {
  en: { nativeName: 'English' },
  es: { nativeName: 'Español' },
  fr: { nativeName: 'Français' },
  de: { nativeName: 'Deutsch' },
  it: { nativeName: 'Italiano' },
  pt: { nativeName: 'Português' },
  ar: { nativeName: 'العربية' },
  nl: { nativeName: 'Dutch' },
  ru: { nativeName: 'Русский' },
  tr: { nativeName: 'Türkçe' },
  pl: { nativeName: 'Polski' },
  id: { nativeName: 'Bahasa Indonesia' }
};

// Helper function to normalize language codes
const normalizeLanguageCode = (code: string): string => {
  return code.split('-')[0];
};

const initOptions: InitOptions = {
  supportedLngs: Object.keys(SUPPORTED_LANGUAGES),
  fallbackLng: 'en',
  defaultNS: 'translation',
  fallbackNS: 'translation',
  debug: process.env.NODE_ENV === 'development',
  
  interpolation: {
    escapeValue: false,
  },
  
  backend: {
    loadPath: '/locales/{{lng}}/translation.json',
    addPath: '/locales/{{lng}}/{{ns}}.missing.json',
    allowMultiLoading: false,
    crossDomain: false,
    withCredentials: false,
    reloadInterval: false
  },
  
  detection: {
    order: ['localStorage', 'navigator'],
    caches: ['localStorage'],
    lookupLocalStorage: 'i18nextLng',
    // Convert language codes with regions to base language codes
    convertDetectedLanguage: normalizeLanguageCode
  },

  react: {
    useSuspense: false,
    bindI18n: 'languageChanged',
    bindI18nStore: '',
    transEmptyNodeValue: '',
    transSupportBasicHtmlNodes: true,
    transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p']
  },

  // Only preload English and browser language
  preload: ['en'],
  
  // Load namespaces
  ns: ['translation'],
  load: 'languageOnly',

  // Return key if translation is missing
  returnNull: false,
  returnEmptyString: false,
  returnObjects: false,
  joinArrays: false,
  
  // Cache settings
  keySeparator: '.',
  nsSeparator: ':',
  pluralSeparator: '_',
  contextSeparator: '_',
};

// Initialize i18next
const initI18next = async () => {
  try {
    // Initialize i18next
    await i18n
      .use(Backend)
      .use(LanguageDetector)
      .use(initReactI18next)
      .init(initOptions);

    // Load only English translations initially
    await i18n.loadNamespaces(['translation']);
    
    // Ensure we're using normalized language codes
    const currentLang = i18n.language;
    const normalizedLang = normalizeLanguageCode(currentLang);
    
    // If the current language has a region code, change to the base language code
    if (currentLang !== normalizedLang && Object.keys(SUPPORTED_LANGUAGES).includes(normalizedLang)) {
      logger.debug(`Normalizing language from ${currentLang} to ${normalizedLang}`);
      await i18n.changeLanguage(normalizedLang);
    }
    
    logger.log('i18next initialization complete');
  } catch (error) {
    logger.error('i18next initialization failed:', error);
  }
};

// Handle initialization
i18n.on('initialized', (options) => {
  logger.log('i18next initialized:', options);
});

i18n.on('languageChanged', (lng) => {
  logger.log('Language changed to:', lng);
});

i18n.on('loaded', (loaded) => {
  logger.log('Translations loaded:', loaded);
});

i18n.on('failedLoading', (lng, ns, msg) => {
  logger.error(`i18next failed loading ${lng}:`, msg);
});

i18n.on('missingKey', (lngs, namespace, key, res) => {
  logger.warn(`Missing translation key: ${key} in namespace: ${namespace} for languages:`, lngs);
});

// Start initialization
initI18next();

export default i18n; 